import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class CommodityErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (process.env.NODE_ENV === 'development') {
      console.error('Commodity page error:', error, errorInfo);
    } else {
      // In production, log minimal info for debugging
      console.error('Application error occurred');
    }
    this.setState({
      hasError: true,
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '40px',
          maxWidth: '800px',
          margin: '0 auto',
          textAlign: 'center',
          color: '#e2f2ff'
        }}>
          <div style={{
            background: 'rgba(21, 24, 41, 0.65)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
            <h1 style={{ color: '#ef4444', marginBottom: '20px' }}>
              Something went wrong
            </h1>
            <p style={{ color: '#cbd5e1', marginBottom: '30px', lineHeight: '1.6' }}>
              We encountered an error while loading this commodity page.
              This could be due to invalid data or a temporary issue.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(180deg, #00e6d2 0%, #00bfae 100%)',
                  color: '#0d0f1b',
                  fontWeight: 700,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Reload Page
              </button>
              <Link
                to="/market-overview"
                style={{
                  padding: '12px 24px',
                  background: 'rgba(0, 230, 210, 0.1)',
                  color: '#00e6d2',
                  fontWeight: 700,
                  border: '1px solid #00e6d2',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  display: 'inline-block',
                  transition: 'all 0.2s'
                }}
              >
                Go to Market Overview
              </Link>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{ marginTop: '30px', textAlign: 'left' }}>
                <summary style={{
                  cursor: 'pointer',
                  color: '#9ca3af',
                  marginBottom: '10px',
                  fontSize: '14px'
                }}>
                  Error Details (Development Only)
                </summary>
                <pre style={{
                  background: '#0b1220',
                  padding: '15px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  overflow: 'auto',
                  color: '#ef4444',
                  border: '1px solid #1d2a44'
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default CommodityErrorBoundary;
